import type { HeaderService, Tenant } from '@patsnap-utils/biz'
import { globalStore } from '@pharmsnap/pharmsnap-web/store/globalStore'
import { computed, defineComponent, onMounted, ref } from '@vue/composition-api'
import { useAuthStore } from 'pharmsnapMF_shared/composition'
import { SwitchTenantGuideDialog } from './SwitchTenantGuideDialog'

const ONE_DAY = 24 * 60 * 60 * 1000
const SWITCH_TENANT_GUIDE_STORAGE_KEY = 'pharmsnap:switch-tenant-guide'

type SwitchTenantGuideStorage = {
  dismissed?: boolean
  snoozeUntil?: number
}

function getStorageKey(userId?: string) {
  return userId ? `${SWITCH_TENANT_GUIDE_STORAGE_KEY}:${userId}` : SWITCH_TENANT_GUIDE_STORAGE_KEY
}

function getStorageState(userId?: string): SwitchTenantGuideStorage {
  try {
    return JSON.parse(localStorage.getItem(getStorageKey(userId)) || '{}')
  } catch {
    return {}
  }
}

function setStorageState(userId: string | undefined, state: SwitchTenantGuideStorage) {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(state))
}

function shouldSkipGuide(userId?: string) {
  const storageState = getStorageState(userId)
  return !!storageState.dismissed || !!(storageState.snoozeUntil && storageState.snoozeUntil > Date.now())
}

export const SwitchTenantGuideDialogEntry = defineComponent({
  name: 'SwitchTenantGuideDialogEntry',
  setup() {
    const {
      state: { boProfileInfo },
    } = useAuthStore()
    const visible = ref(false)
    const tenants = ref<Tenant[]>([])
    const userId = computed(() => boProfileInfo.value?.id)
    const shouldShowGuide = computed(() => {
      return !!boProfileInfo.value?.individual && tenants.value.some((item) => !!item.tenant_id)
    })

    const headerServiceSyncEnd = (): Promise<HeaderService> => {
      return new Promise((resolve) => {
        const timer = setInterval(() => {
          const headerService = globalStore.headerService as HeaderService | null
          if (headerService && !headerService.syncing) {
            clearInterval(timer)
            resolve(headerService)
          }
        }, 100)
      })
    }

    const tryShowGuide = () => {
      if (!shouldShowGuide.value || shouldSkipGuide(userId.value)) {
        return
      }

      visible.value = true
    }

    const closeGuide = () => {
      visible.value = false
    }

    const handleCancel = (doNotShowAgain: boolean) => {
      setStorageState(
        userId.value,
        doNotShowAgain
          ? { dismissed: true }
          : {
              // 选择继续使用个人租户，1天内不再提示
              snoozeUntil: Date.now() + ONE_DAY,
            }
      )
      closeGuide()
    }

    const handleSwitchTenant = (doNotShowAgain: boolean) => {
      if (doNotShowAgain) {
        setStorageState(userId.value, { dismissed: true })
      }
      closeGuide()
    }

    onMounted(() => {
      headerServiceSyncEnd().then((headerService) => {
        tenants.value = headerService.tenants || []
        tryShowGuide()
      })
    })

    return {
      visible,
      handleCancel,
      handleSwitchTenant,
    }
  },
  render() {
    return <SwitchTenantGuideDialog visible={this.visible} onCancel={this.handleCancel} onSwitchTenant={this.handleSwitchTenant} />
  },
})
